import { Col, Row, Modal } from 'antd';
import Button from 'components/ui/base/button';
import SearchBar from 'components/ui/base/searchbar';
import { FC, useCallback, useEffect, useReducer } from 'react';
import ProductTemplatesTable from 'views/custom-declaration/products/components/ProductTemplatesTable';
import useFormUtils from 'hooks/useFormUtils';
import useProductTemplateList from 'views/templates/hooks/useProductTemplateList';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { ListProductTemplateParams } from 'store/products-templates/client';
import debounce from 'lodash.debounce';
import { TemplateResponse } from '../../../store/template/template';
import { isEmpty } from 'lodash';

interface Props {
    addProductsToDeclaration?: Function;
    declarationTemplate: TemplateResponse | undefined;
}

export interface AddProductTemplatesToDeclaration {
    modalVisible: boolean;
    productTemplatesIds: string[];
}

const DeclarationsProductsTemplates: FC<Props> = ({ addProductsToDeclaration, declarationTemplate }) => {
    const { countryLowerCase, internalType, formType } = useFormUtils();
    // The table asks for its own pages and sorting, and this component adds the default declaration template filter.
    const {
        productTemplates,
        isLoading,
        refetch: listProductTemplates,
    } = useProductTemplateList({
        country: countryLowerCase as DeclarationCountry,
        internalType: internalType as DeclarationInternalType,
        messageType: formType as MessageTypes,
        autoFetch: false,
    });

    const [addProductTemplatesToDeclaration, setAddProductTemplatesToDeclaration] = useReducer(
        (initialState: AddProductTemplatesToDeclaration, newState: Partial<AddProductTemplatesToDeclaration>) => ({
            ...initialState,
            ...newState,
        }),
        { modalVisible: false, productTemplatesIds: [] }
    );
    const closeModal = useCallback(() => setAddProductTemplatesToDeclaration({ modalVisible: false }), []);

    const listProductTemplatesRequest = useCallback(
        (params?: Partial<ListProductTemplateParams>) =>
            listProductTemplates({ ...params, declarationTemplateId: 'default' }),
        [listProductTemplates]
    );

    useEffect(() => {
        listProductTemplatesRequest();
    }, [listProductTemplatesRequest]);

    const querySearch = (query: string) => {
        const params = query ? { query } : {};
        listProductTemplatesRequest(params);
    };

    const debouncedSearch = debounce((query: string) => querySearch(query), 500);

    return (
        <div style={{ paddingRight: '0.8rem' }}>
            <Row justify="end" style={{ marginBottom: '2.5rem' }} wrap={false}>
                <Button
                    disabled={isEmpty(addProductTemplatesToDeclaration.productTemplatesIds)}
                    onClick={() => {
                        addProductsToDeclaration?.(addProductTemplatesToDeclaration.productTemplatesIds);
                        setAddProductTemplatesToDeclaration({ productTemplatesIds: [] });
                    }}
                >
                    Add to declaration
                </Button>
            </Row>

            <Row gutter={16} style={{ marginBottom: '2.5rem' }}>
                <Col span={24}>
                    <SearchBar
                        inputPlaceholder="Search products"
                        onClear={() => listProductTemplatesRequest()}
                        onSearch={(value) => debouncedSearch(value)}
                    />
                </Col>
            </Row>

            <ProductTemplatesTable
                source={productTemplates}
                loading={isLoading}
                onRequestList={listProductTemplatesRequest}
                // declarationTemplatesList is used to evaluate the declaration template names in the table
                declarationTemplatesList={declarationTemplate ? [declarationTemplate] : []}
                onAddProductTemplatesToDeclaration={setAddProductTemplatesToDeclaration}
            />
            <Modal
                visible={addProductTemplatesToDeclaration.modalVisible}
                onCancel={closeModal}
                title="Add to declaration"
                footer={[
                    <Button onClick={closeModal}>Cancel</Button>,
                    <Button
                        onClick={() => {
                            addProductsToDeclaration?.(addProductTemplatesToDeclaration.productTemplatesIds);
                            closeModal();
                        }}
                    >
                        Confirm
                    </Button>,
                ]}
            >
                Please confirm you want to add this Product to the Declaration.
            </Modal>
        </div>
    );
};
export default DeclarationsProductsTemplates;
